import { I18n } from '@affine/i18n';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import {
  streamLainDisclosureStyles,
  syncStreamLainDisclosureOverlayGeometry,
} from '@blocksuite/affine-shared/utils';
import {
  AttachmentIcon,
  DatabaseTableViewIcon,
  ImageIcon,
  TextIcon,
  UngroupIcon,
} from '@blocksuite/icons/lit';
import { offset, shift } from '@floating-ui/dom';
import { css, html, type TemplateResult, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { CanvasSelectionItem } from '../ai-chat-content/type';
import { streamLainDropdownMenuSizeMiddleware } from '../ai-chat-input/dropdown-menu-chrome';
import {
  getChatInputPortalContainer,
  getChatInputPortalOverlayWidth,
  getChatInputPortalReferenceElement,
  getChatInputPortalStyle,
} from './chat-input-portal-style';
import type { SelectedContextChip } from './type';
import { getChipIcon, getChipTooltip } from './utils';

export function flattenCanvasItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  const out: CanvasSelectionItem[] = [];
  for (const item of items) {
    out.push(item);
    if (!isAtomicCanvasItem(item) && item.children?.length) {
      out.push(...flattenCanvasItems(item.children));
    }
  }
  return out;
}

export function isAtomicCanvasItem(item: CanvasSelectionItem): boolean {
  return (
    item.role === 'database' ||
    item.role === 'pdf' ||
    item.role === 'attachment' ||
    item.type === 'affine:image' ||
    item.type === 'affine:database' ||
    item.type === 'affine:attachment' ||
    item.type === 'affine:note'
  );
}

export function getCanvasSelectionIcon(
  item: CanvasSelectionItem
): TemplateResult<1> {
  if (item.role === 'database' || item.type === 'affine:database') {
    return DatabaseTableViewIcon();
  }
  if (
    item.role === 'pdf' ||
    item.role === 'attachment' ||
    item.type === 'affine:attachment'
  ) {
    return AttachmentIcon();
  }
  if (item.type === 'affine:image') {
    return ImageIcon();
  }
  if (
    item.type === 'affine:note' ||
    item.type === 'affine:paragraph' ||
    item.type === 'text'
  ) {
    return TextIcon();
  }
  return UngroupIcon();
}

export class ChatPanelSelectedChip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = [
    unsafeCSS(streamLainDisclosureStyles),
    css`
      :host {
        display: inline-flex;
        max-width: 100%;
        min-width: 0;
      }

      .selected-context-input-shell {
        max-width: 100%;
      }

      .selected-context-input-popover chat-panel-context-group-portal {
        max-width: 100%;
      }
    `,
  ];

  @property({ attribute: false })
  accessor chip!: SelectedContextChip;

  @property({ attribute: false })
  accessor removeChip: ((chip: SelectedContextChip) => void) | undefined;

  @property({ attribute: false })
  accessor readOnly = false;

  @property({ attribute: false })
  accessor removeCanvasSelectionItem:
    | ((chip: SelectedContextChip, itemId: string) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItems:
    | ((chip: SelectedContextChip, itemIds: string[]) => void)
    | undefined;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @query('chat-panel-chip')
  accessor chipElement!: HTMLElement;

  private _abortController: AbortController | null = null;

  private _overlayResizeObserver: ResizeObserver | null = null;
  private _portalPositionUpdate: (() => void) | null = null;
  private _portalRenderUpdate: (() => void) | null = null;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._closePortal();
  }

  override render() {
    const { state } = this.chip;
    const isLoading = state === 'processing';
    const name =
      this.chip.name ?? I18n.t('com.streamlain.ai.context.canvas.default');
    const tooltip = getChipTooltip(state, name, this.chip.tooltip);

    const icon = getChipIcon(state, UngroupIcon());

    const expanded = Boolean(this._abortController);
    const chip = html`<chat-panel-chip
      .state=${state}
      .name=${name}
      .tooltip=${tooltip}
      .icon=${icon}
      .closeable=${!this.readOnly && !isLoading}
      .expandable=${this._isExpandable()}
      .expanded=${expanded}
      .expandDirection=${'up'}
      .onChipDelete=${this.onChipDelete}
      .onChipClick=${this._handleChipClick}
    ></chat-panel-chip>`;

    return html`<span
      class="selected-context-input-shell streamlain-disclosure-overlay-anchor streamlain-disclosure-overlay-up"
    >
      ${chip}
    </span>`;
  }

  private _getItems() {
    return (this.chip.canvasSelections || []).flatMap(selection =>
      selection.items.map(item => item)
    );
  }

  private _isExpandable() {
    const items = this._getItems();
    if (items.length === 1 && isAtomicCanvasItem(items[0])) {
      return false;
    }

    return flattenCanvasItems(items).length > 1;
  }

  private _contextGroupItems() {
    const children = this._getItems();
    const flatChildren = flattenCanvasItems(children);
    return [
      {
        kind: 'canvas' as const,
        key: `canvas:${this.chip.uuid}`,
        label:
          this.chip.name ?? I18n.t('com.streamlain.ai.context.canvas.default'),
        meta:
          flatChildren.length > children.length
            ? I18n.t('com.streamlain.ai.context.selected-contained', {
                selected: children.length,
                total: flatChildren.length,
              })
            : I18n.t('com.streamlain.ai.context.selected', {
                count: children.length,
              }),
        removable: true,
        chip: this.chip,
        children,
      },
    ];
  }

  private _closePortal() {
    this._abortController?.abort();
    this._abortController = null;
    this._portalPositionUpdate = null;
    this._portalRenderUpdate = null;
    this._disconnectOverlayResizeObserver();
    this.style.removeProperty('--streamlain-disclosure-overlay-width');
    this.style.removeProperty('--streamlain-disclosure-overlay-shift-x');
    this.requestUpdate();
  }

  private _getOverlayBoundary(): Element {
    return this._getPortalReferenceElement();
  }

  private _getPortalReferenceElement(): HTMLElement {
    return getChatInputPortalReferenceElement(this, this.chipElement);
  }

  private _getPortalContainer(): Element {
    return getChatInputPortalContainer(this, this.portalContainer);
  }

  private _getPortalOverlayStyle() {
    return getChatInputPortalStyle(
      this,
      getChatInputPortalOverlayWidth(this._getPortalReferenceElement())
    );
  }

  private readonly _syncOverlayGeometry = () => {
    if (!this._abortController || !this.chipElement) return;

    syncStreamLainDisclosureOverlayGeometry({
      boundary: this._getOverlayBoundary(),
      host: this,
      trigger: this.chipElement,
    });
    this._portalRenderUpdate?.();
    this._portalPositionUpdate?.();
  };

  private readonly _handlePortalLayoutChange = () => {
    this._syncOverlayGeometry();
  };

  private _renderPortal() {
    return html`<span
      class="selected-context-input-popover"
      style=${styleMap(this._getPortalOverlayStyle())}
    >
      <chat-panel-context-group-portal
        input-surface
        .items=${this._contextGroupItems()}
        .readOnly=${this.readOnly}
        @context-group-layout-change=${this._handlePortalLayoutChange}
        @remove-context-item=${this._handleRemoveContextItem}
      ></chat-panel-context-group-portal>
    </span>`;
  }

  private _disconnectOverlayResizeObserver() {
    this._overlayResizeObserver?.disconnect();
    this._overlayResizeObserver = null;
  }

  private _observeOverlayBoundary(signal: AbortSignal) {
    if (typeof ResizeObserver === 'undefined') return;
    this._disconnectOverlayResizeObserver();
    const boundary = this._getOverlayBoundary();
    const resizeObserver = new ResizeObserver(() => {
      this._syncOverlayGeometry();
    });
    resizeObserver.observe(boundary);
    this._overlayResizeObserver = resizeObserver;
    signal.addEventListener(
      'abort',
      () => this._disconnectOverlayResizeObserver(),
      { once: true }
    );
  }

  private readonly _handleRemoveContextItem = (
    event: CustomEvent<{ itemId?: string; itemIds?: string[] }>
  ) => {
    if (this.readOnly) return;
    event.preventDefault();
    event.stopPropagation();

    const itemIds = [...(event.detail.itemIds ?? [])];
    if (event.detail.itemId) {
      itemIds.push(event.detail.itemId);
    }

    if (itemIds.length > 1 && this.removeCanvasSelectionItems) {
      this.removeCanvasSelectionItems(this.chip, itemIds);
    } else if (itemIds.length > 0) {
      for (const itemId of itemIds) {
        this.removeCanvasSelectionItem?.(this.chip, itemId);
      }
    } else {
      this.removeChip?.(this.chip);
    }

    requestAnimationFrame(() => {
      if (!this._getItems().length) {
        this._closePortal();
      } else {
        this.requestUpdate();
        this._portalRenderUpdate?.();
        this._syncOverlayGeometry();
      }
    });
  };

  private readonly _togglePortal = () => {
    const items = this._getItems();
    if (!items.length || !this._isExpandable()) return;
    if (this._abortController) {
      this._closePortal();
      return;
    }

    const abortController = new AbortController();
    abortController.signal.addEventListener('abort', () => {
      this._abortController = null;
      this._portalPositionUpdate = null;
      this._portalRenderUpdate = null;
      this.style.removeProperty('--streamlain-disclosure-overlay-width');
      this.style.removeProperty('--streamlain-disclosure-overlay-shift-x');
      this.requestUpdate();
    });
    document.addEventListener(
      'keydown',
      event => {
        if (event.key === 'Escape') {
          this._closePortal();
        }
      },
      { signal: abortController.signal }
    );
    window.addEventListener('resize', this._syncOverlayGeometry, {
      signal: abortController.signal,
    });
    this._abortController = abortController;
    this._observeOverlayBoundary(abortController.signal);
    const { update } = createLitPortal({
      template: ({ updatePortal }) => {
        this._portalRenderUpdate = updatePortal;
        return this._renderPortal();
      },
      portalStyles: {
        zIndex: 'var(--streamlain-z-index-popover)',
      },
      container: this._getPortalContainer(),
      positionStrategy: 'fixed',
      computePosition: {
        referenceElement: this._getPortalReferenceElement(),
        placement: 'top-end',
        middleware: [
          offset({ mainAxis: 8, crossAxis: 0 }),
          streamLainDropdownMenuSizeMiddleware({ minHeight: 0 }),
          shift({ padding: 16 }),
        ],
        autoUpdate: true,
      },
      abortController,
      closeOnClickAway: true,
    });
    this._portalPositionUpdate = update;
    this.requestUpdate();
    requestAnimationFrame(() => this._syncOverlayGeometry());
  };

  private readonly _handleChipClick = () => {
    this._togglePortal();
  };

  private readonly onChipDelete = () => {
    if (this.readOnly) return;
    this._closePortal();
    this.removeChip?.(this.chip);
  };
}
